import React from 'react';

import { Box, FormControlLabel, FormGroup, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { CollectionInput } from '@/components/DynamicForm/types/DynamicFormTypes';
import ImageWrapper from '@/components/ImageWrapper';
import { applyLayoutStyles } from '@/components/DynamicForm/layout.styles.handler';
import { publish } from '@/components/DynamicForm/events';

import {
	getFormControlLabelStyles,
	getFormGroupStyles,
	getFormLabelBoxWrapper,
	getSelectedOptionIconStyles,
	imageStyles,
} from './SingleAndMultiSelect.styles';

interface IQuestions extends CollectionInput {
	eyebrow?: string;
	description?: string;
}

export const SingleAndMultiSelect = ({
	errMsg,
	name,
	label,
	eyebrow,
	value = [],
	description,
	options = [],
	showError,
	layout,
	optionsLayout = {},
	validations,
	reset,
	type,
}: IQuestions) => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

	const selectedValues = Array.isArray(value) ? value : [value];

	return (
		<Grid container direction="column" justifyContent="center" alignItems="center" gap={2}>
			<Grid item>
				<Typography variant="header2" color="text.primary" textAlign="center">
					{label}
				</Typography>
			</Grid>

			{description ? (
				<Grid item textAlign="center">
					<Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}>{description}</Typography>
				</Grid>
			) : null}

			{eyebrow ? (
				<Grid item>
					<Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}>{eyebrow}</Typography>
				</Grid>
			) : null}

			{showError ? (
				<Grid item>
					<Typography variant="notification" color="error.dark" data-testid="question_field_error_message">
						{errMsg}
					</Typography>
				</Grid>
			) : null}

			<Grid item width="100%">
				<FormGroup sx={applyLayoutStyles(getFormGroupStyles, layout)}>
					{options.map((option) => {
						const isOptionSelected = selectedValues.includes(option.value);
						const selectedOptionFontVariant = isSmallScreen ? 'bodyMediumSemiBold' : 'bodyLargeSemiBold';
						const defaultOptionFontVariant = isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook';
						return (
							<FormControlLabel
								key={option.value}
								sx={getFormControlLabelStyles}
								labelPlacement="end"
								onClick={() => {
									publish('formDataUpdate', [name, option.value, validations, reset, type]);
									publish('markInteracted', name);
								}}
								onBlur={() => publish('markInteracted', name)}
								label=""
								control={
									<Box
										sx={applyLayoutStyles(
											getFormLabelBoxWrapper(theme, isOptionSelected, !!option?.imageUrl),
											{
												...optionsLayout,
												...option.layout,
											}
										)}
										component="div"
										data-testid={`option_${option.value}`}
									>
										{option.imageUrl && <ImageWrapper src={option.imageUrl} sx={imageStyles} />}
										<Typography
											color="text.interactive"
											variant={
												isOptionSelected ? selectedOptionFontVariant : defaultOptionFontVariant
											}
											textAlign="center"
										>
											{option.label}
										</Typography>
										{isOptionSelected ? (
											<CheckCircleIcon color="secondary" sx={getSelectedOptionIconStyles} />
										) : null}
									</Box>
								}
							/>
						);
					})}
				</FormGroup>
			</Grid>
		</Grid>
	);
};
